import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { calculateTextWidth } from 'shared/util-common';

import { isIconTagType } from './tag.util';
import { FlowchartConfig, FontAttributes } from '../base/flowchart.config';

export interface NodePosition {
  x: number;
  y: number;
}

interface PlainRect {
  x?: number;
  y?: number;
  width: number;
  height: number;
}

type BBox = PlainRect;
type SourceElementSize = Pick<BBox, 'width' | 'height'>;

@Injectable({
  providedIn: 'root',
})
export class PositionService {
  calculateStepPosition(
    sourceElementSize: SourceElementSize,
    sourceElementPosition: NodePosition,
    sourceConnector,
    newNodeSize: PlainRect,
  ): NodePosition {
    // TODO: Possible Improvement: Make a new calculation based completely on the new element's estimated size.
    let positionDifferenceX: number;
    let positionDifferenceY: number;
    let baseElementDistance = FlowchartConfig.baseElementDistance;

    switch (sourceConnector.name) {
      case 'top':
        baseElementDistance = baseElementDistance + sourceElementSize.height / 2;
        positionDifferenceX = sourceElementSize.width / 2 - newNodeSize.width / 2;
        positionDifferenceY = -baseElementDistance - newNodeSize.height + sourceElementSize.height / 2;
        break;
      case 'right':
        baseElementDistance = baseElementDistance + sourceElementSize.width / 2;
        positionDifferenceX = baseElementDistance + sourceElementSize.width - sourceElementSize.width / 2;
        positionDifferenceY = (sourceElementSize.height - newNodeSize.height) / 2;
        break;
      case 'bottom':
        baseElementDistance = baseElementDistance + sourceElementSize.height / 2;
        positionDifferenceX = sourceElementSize.width / 2 - newNodeSize.width / 2;
        positionDifferenceY = baseElementDistance;
        break;
      case 'left':
        baseElementDistance = baseElementDistance + sourceElementSize.width / 2;
        positionDifferenceX = -baseElementDistance - newNodeSize.width + sourceElementSize.width / 2;
        positionDifferenceY = (sourceElementSize.height - newNodeSize.height) / 2;
        break;
      default:
        positionDifferenceX = Math.abs(sourceElementSize.width - newNodeSize.width) / 2;
        positionDifferenceY = baseElementDistance + sourceElementSize.height - sourceElementSize.height / 2;
        break;
    }
    return {
      x: sourceElementPosition.x + positionDifferenceX,
      y: sourceElementPosition.y + positionDifferenceY,
    };
  }

  calculateStartPosition(paperSize: PlainRect): NodePosition {
    const gridSize = FlowchartConfig.paper.gridSize;
    return {
      x: (paperSize.width - FlowchartConfig.nodeSize.default.width) / 2,
      y: Math.round(paperSize.height / 5 / gridSize) * gridSize - FlowchartConfig.nodeSize.default.height,
    };
  }

  getContainerHeaderPosition(x: number, y: number) {
    const containerConfiguration = FlowchartConfig.containerNode;

    return {
      x: x + containerConfiguration.styles.headerNegativePadding,
      y: y + containerConfiguration.styles.headerNegativePadding,
    };
  }

  getContainerSelectionBehaviorPosition(x: number, y: number, containerWidth: number) {
    const containerConfiguration = FlowchartConfig.containerNode;

    return {
      x:
        x +
        containerWidth -
        FlowchartConfig.selectionBehaviorNode.size.width +
        containerConfiguration.styles.selectionBehaviorHorizontalPadding,
      y: y + containerConfiguration.styles.selectionBehaviorVerticalPadding,
    };
  }

  calculateTagXPosition(x: number, index: number, tagsArray: string[], translocoService: TranslocoService): number {
    let xNew = FlowchartConfig.tag.padding.horizontalPaddingStart;
    tagsArray.forEach((tag, i) => {
      if (i < index) {
        if (isIconTagType(tag)) {
          xNew += FlowchartConfig.tag.size.iconTagWidth + FlowchartConfig.tag.padding.paddingBetweenTags;
        } else {
          //use correct translation
          xNew +=
            calculateTextWidth(translocoService.translate(tag), FontAttributes.fontFamily, '12px') +
            FlowchartConfig.tag.padding.horizontalPadding +
            FlowchartConfig.tag.padding.paddingBetweenTags;
        }
      }
    });
    return xNew + x;
  }
}
